import React, { useState } from 'react';
import { AlertTriangle, Upload, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { useDisputes } from '@/hooks/useDisputes';
import { DISPUTE_TYPES, DisputeType } from '@/lib/types/dispute';
import { useToast } from '@/hooks/use-toast';

interface FileDisputeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobTitle: string;
  onDisputeFiled?: () => void;
}

export const FileDisputeModal: React.FC<FileDisputeModalProps> = ({
  open,
  onOpenChange,
  jobId,
  jobTitle,
  onDisputeFiled,
}) => {
  const { createDispute } = useDisputes();
  const { toast } = useToast();
  
  const [disputeType, setDisputeType] = useState<DisputeType | ''>('');
  const [reason, setReason] = useState('');
  const [requestedResolution, setRequestedResolution] = useState('');
  const [confirmedAttemptedResolution, setConfirmedAttemptedResolution] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const minReasonLength = 50;
  const isReasonValid = reason.trim().length >= minReasonLength;
  const canSubmit = disputeType && isReasonValid && confirmedAttemptedResolution && !isSubmitting;

  const resetForm = () => {
    setDisputeType('');
    setReason('');
    setRequestedResolution('');
    setConfirmedAttemptedResolution(false);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    
    setIsSubmitting(true);
    try {
      await createDispute({
        job_id: jobId,
        reason: reason.trim(),
        dispute_type: disputeType,
        requested_resolution: requestedResolution.trim() || undefined,
      });
      
      resetForm();
      onOpenChange(false);
      onDisputeFiled?.();
    } catch (error) {
      console.error('Failed to file dispute:', error);
      toast({
        title: 'Failed to file dispute',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            File a Dispute
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Job reference */}
          <div className="text-sm">
            <span className="text-muted-foreground">Job: </span>
            <span className="font-medium">{jobTitle}</span>
          </div>

          {/* Direct Resolution Notice */}
          <Alert className="border-blue-200 bg-blue-50">
            <AlertTriangle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-900">
              <p className="font-medium mb-2">Before Filing a Dispute</p>
              <p className="text-sm">
                We encourage all parties to first attempt to resolve issues directly through 
                the job messaging system. Disputes should only be filed when direct communication 
                has not led to a mutually agreeable resolution.
              </p>
            </AlertDescription>
          </Alert>

          {/* Resolution Attempt Confirmation */}
          <div className="flex items-start space-x-3 p-3 rounded-lg border bg-muted/30">
            <Checkbox 
              id="resolution-attempt" 
              checked={confirmedAttemptedResolution}
              onCheckedChange={(checked) => setConfirmedAttemptedResolution(checked === true)}
            />
            <Label 
              htmlFor="resolution-attempt" 
              className="text-sm leading-relaxed cursor-pointer"
            >
              I confirm that I have attempted to resolve this issue directly with the other 
              party and we were unable to reach a mutually agreeable resolution.
            </Label>
          </div>

          {/* Dispute Type */}
          <div className="space-y-2">
            <Label htmlFor="dispute-type">
              Dispute Type <span className="text-destructive">*</span>
            </Label>
            <Select 
              value={disputeType} 
              onValueChange={(value) => setDisputeType(value as DisputeType)}
            >
              <SelectTrigger id="dispute-type">
                <SelectValue placeholder="Select the type of dispute" />
              </SelectTrigger>
              <SelectContent className="bg-background">
                {DISPUTE_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex flex-col">
                      <span>{type.label}</span>
                      <span className="text-xs text-muted-foreground">{type.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="reason">
                Describe the Issue <span className="text-destructive">*</span>
              </Label>
              <span className={`text-xs ${isReasonValid ? 'text-muted-foreground' : 'text-orange-600'}`}>
                {reason.trim().length}/{minReasonLength} min
              </span>
            </div>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Provide a detailed description of the issue, including relevant dates, quantities, and specific concerns..."
              rows={4}
              className="resize-none"
            />
            {reason.length > 0 && !isReasonValid && (
              <p className="text-xs text-orange-600">
                Please provide at least {minReasonLength} characters describing the issue.
              </p>
            )}
          </div>

          {/* Requested Resolution */}
          <div className="space-y-2">
            <Label htmlFor="resolution">
              Requested Resolution <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="resolution"
              value={requestedResolution}
              onChange={(e) => setRequestedResolution(e.target.value)}
              placeholder="Describe how you would like this issue to be resolved..."
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Warning */}
          <Alert className="border-orange-200 bg-orange-50">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-900 text-sm">
              Filing a dispute will pause payment processing and notify platform administrators 
              for review. Both parties will be contacted for additional information.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button 
            variant="outline" 
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {isSubmitting ? 'Filing Dispute...' : 'File Dispute'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
